import React from 'lib/react';
import {
  EVPaper,
  EVGrid,
  EVAlert,
  EVChip,
} from 'lib/components';
import { useTranslation } from 'lib/translation';
import useMediaQuery from 'hooks/useMediaQuery';
import { useSelector, useDispatch } from 'lib/redux';
import productTooltips from 'features/order/components/Product/productTooltips.json';
import ProductTooltip from 'features/order/components/Product/ProductTooltip.component';
import { getTooltipForProduct } from 'features/order/components/Product/ProductList.util';
import Map from 'commonComponents/map';
import { updateFocusedPointIndex } from 'features/order/Order.actions';
import clsx from 'lib/clsx';
import Typography from 'commonComponents/typography';
import UpgradedReportStyles from './UpgradedReportStyles';

function UpgradedReport() {
  const classes = UpgradedReportStyles();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(640);
  const {
    upgradedReportId, upgradedReportAddress, upgradedReportDate, upgradedStructureType, upgradedProduct, upgradedReportStatus,
  } = useSelector((state) => state.finalizeOrder);

  const dispatch = useDispatch();
  const handleFocusChange = (index) => dispatch(updateFocusedPointIndex(index));

  const statusClassName = {
    Completed: classes.completed,
    Closed: classes.closed,
    InProcess: classes.inProcess,
    Created: classes.created,
    ActionNeeded: classes.actionNeeded,
    CheckEmail: classes.checkEmail,
  };
  const { address } = upgradedReportAddress[0];
  const upgradeAddress = `${address.address}, ${address.city}, ${address.state} ${address.zip}`;

  return (
    <>
      <EVAlert
        severity="warning"
        className={classes.alert}
        classes={{
          icon: classes.warningAlertIcon, message: classes.alertMsg, root: classes.warningAlert, action: clsx(classes.closeBtn, classes.errorAlertIcon),
        }}
      >
        <Typography varaint="subtitle-600" className="title">{t('upgraded__alert_title')}</Typography>
        <Typography variant="subtitle-400" className="message">{t('upgraded__alert_message')}</Typography>
      </EVAlert>
      <EVPaper square={isMobile} className={classes.mainPaper}>
        <EVGrid className={classes.detailsMapGrid}>
          <EVPaper className={classes.mapGrid} elevation={0}>
            <Map
              center={address}
              mapPoints={upgradedReportAddress}
              propertyType={upgradedStructureType}
              onFocusChange={handleFocusChange}
              mapOptions={{ showMapTypeSelector: false, showScalebar: false }}
            />
          </EVPaper>
          <EVGrid container className={classes.detailsContent}>
            <EVGrid className={classes.detailsContentBlueContainer}>
              <EVGrid style={{ display: 'flex', flexDirection: 'row' }}>
                <Typography
                  fontColor="ocean-700"
                  variant="label-600"
                  style={{
                    textTransform: 'uppercase',
                    width: 'fit-content',
                    marginRight: '40px',
                  }}
                  data-testid="upgrade-to"
                >
                  {t('upgraded__to')}
                </Typography>
                <EVChip
                  className={clsx(classes.statusChip, statusClassName[upgradedReportStatus])}
                  label={upgradedReportStatus}
                />
              </EVGrid>
              <ProductTooltip title={getTooltipForProduct(upgradedProduct) ?? ''}>
                <Typography
                  variant="h3-600"
                  fontColor="ocean-700"
                  style={{
                    wordWrap: 'anywhere',
                  }}
                >
                  {productTooltips[upgradedProduct]?.name}
                </Typography>
              </ProductTooltip>
            </EVGrid>
            <EVGrid className={classes.detailsContentContainer}>
              <Typography
                fontColor="charcoal-800"
                variant="label-600"
                className={classes.detailsContentTitleText}
              >
                {t('PropertyType')}
              </Typography>
              <Typography
                fontColor="charcoal-900"
                variant="subtitle-400"
              >
                {upgradedStructureType}
              </Typography>
            </EVGrid>
            <EVGrid className={classes.detailsContentContainer}>
              <Typography
                fontColor="charcoal-800"
                variant="label-600"
                className={classes.detailsContentTitleText}
              >
                {t('BuildingId')}
              </Typography>
              <Typography
                fontColor="charcoal-900"
                variant="subtitle-400"
              >
                {upgradeAddress}
              </Typography>
            </EVGrid>
            <EVGrid className={classes.detailsContentContainer}>
              <Typography
                fontColor="charcoal-800"
                variant="label-600"
                className={classes.detailsContentTitleText}
              >
                {t('order_history__report_id')}
              </Typography>
              <Typography
                fontColor="charcoal-900"
                variant="subtitle-400"
              >
                {upgradedReportId}
              </Typography>
            </EVGrid>
            <EVGrid className={classes.detailsContentContainer}>
              <Typography
                fontColor="charcoal-800"
                variant="label-600"
                className={classes.detailsContentTitleText}
              >
                {t('common__date')}
              </Typography>
              <Typography
                fontColor="charcoal-900"
                variant="subtitle-400"
              >
                {upgradedReportDate}
              </Typography>
            </EVGrid>
          </EVGrid>
        </EVGrid>
        <EVGrid className={classes.infoText}>
          <Typography
            fontColor="ocean-900"
            variant="subtitle-400"
            className={classes.clickable}
          >
            {t('upgraded__info_text_1')}
          </Typography>
          <Typography
            fontColor="ocean-900"
            variant="subtitle-400"
            className={classes.clickable}
          >
            {t('upgraded__info_text_2')}
            <b>{t('upgraded__info_text_3')}</b>
          </Typography>
        </EVGrid>
      </EVPaper>
    </>
  );
}

export default UpgradedReport;
